import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MONGODB_AWSGW_QUERIES, MONGODB_AWSGW_APIKEY } from "../utility/keys";
import {
  cwSourceFromGroup,
  TIME_WINDOWS,
  DEFAULT_WINDOW_ID,
} from "./logs/logSources";
import CwLogsBrowser from "./logs/CwLogsBrowser";
import LogsErrorBoundary from "./logs/LogsErrorBoundary";
import { LogVolumeChartModal } from "../components/UserChartModal";
import { LogErrorsChartModal } from "../components/QueryPerformanceModal";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// LogsScreen (#247) — the in-app CloudWatch log browser.
//
// Was the dormant realm→TanStack query-comparison instrument (Oct 2025–Jan 2026);
// its comparison data has no live consumer. The chrome (screen shell, filter row,
// expandable rows, chart buttons, the "logs" access token + header button) is
// kept and now browses AWS Lambda logs — the thing #245 proved the value of when
// a raw `handler event:` payload cracked the `reserved`-vs-`reserve` bug.
//
// Reads go through the queries Lambda's `cwLogs` proxy, so AWS credentials never
// reach the browser. The GROUP LIST is server-owned too (`cwLogGroups`), so
// growing from three Lambdas to all ~20-30 is a Lambda-side array edit with no
// client change and no redeploy of this app.
//
// NO ENV SPLIT, deliberately: one Lambda serves both UAT and PROD, so a group's
// events are a single intermixed stream. A UAT/PROD toggle would imply a
// separation that does not exist.
//
// Compass is deliberately untouched — it is a Mongo/Convex DATA browser and has
// nothing to do with logging.
//
// (An earlier draft of this screen also browsed FlooringXP's app telemetry from
// Convex. That moved into FlooringXP's own admin screen — this is CloudWatch-only.)
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function Btn({ active, disabled, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="px-3 py-1 border-2 border-skin-base rounded-sm text-sm disabled:opacity-40 hover:bg-skin-base-hover"
      style={
        active
          ? { backgroundColor: "rgb(var(--color-button-accent))", color: "#000" }
          : undefined
      }
    >
      {children}
    </button>
  );
}

export const LogsScreen = () => {
  // Server-owned log-group list → source picker entries.
  const {
    data: groupsData,
    isLoading: groupsLoading,
    error: groupsError,
  } = useQuery({
    queryKey: ["cwLogGroups"],
    queryFn: async () => {
      const resp = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "x-api-key": MONGODB_AWSGW_APIKEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ method: "cwLogGroups" }),
      });
      const json = await resp.json();
      return json?.message?.result ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const sources = useMemo(
    () => (groupsData?.groups || []).map(cwSourceFromGroup).filter(Boolean),
    [groupsData]
  );

  // No source is selected until the server list arrives; the first group is
  // adopted once it does. Deriving it (rather than storing a default id that
  // might not exist) keeps the picker honest if the server list changes.
  const [selectedId, setSelectedId] = useState(null);
  const source = sources.find((s) => s.id === selectedId) || sources[0] || null;

  const [windowId, setWindowId] = useState(DEFAULT_WINDOW_ID);
  const [keyword, setKeyword] = useState("");
  const [keywordApplied, setKeywordApplied] = useState("");

  const [volumeOpen, setVolumeOpen] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);

  return (
    <div className="flex flex-col px-4 pt-6 gap-3 h-full relative">
      {/* Log-group picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-skin-primary">Log group:</span>
        {groupsLoading && (
          <span className="text-sm text-skin-link italic">loading groups…</span>
        )}
        {!groupsLoading && !sources.length && (
          <span className="text-sm text-skin-link italic">
            {groupsError
              ? "Couldn’t reach the log-group list."
              : "No log groups available."}
          </span>
        )}
        {sources.map((s) => (
          <Btn
            key={s.id}
            active={source && s.id === source.id}
            onClick={() => setSelectedId(s.id)}
            title={s.label}
          >
            {s.label}
          </Btn>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <button
            className="text-2xl px-2 py-1 rounded-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
            onClick={() => setVolumeOpen(true)}
            disabled={!source}
            title="Log volume / activity chart"
          >
            📊
          </button>
          <button
            className="text-2xl px-2 py-1 rounded-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-40"
            onClick={() => setErrorsOpen(true)}
            disabled={!source}
            title="Top events & errors chart"
          >
            ⚡
          </button>
        </span>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <label className="flex items-center gap-1">
          <span className="text-skin-link">Window</span>
          <select
            className="text-black rounded-sm px-1 py-0.5 border-2 border-dark-grey"
            value={windowId}
            onChange={(e) => setWindowId(e.target.value)}
          >
            {TIME_WINDOWS.map((w) => (
              <option key={w.id} value={w.id}>
                {w.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span className="text-skin-link">Keyword</span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setKeywordApplied(keyword.trim());
            }}
            placeholder='e.g. ERROR or "reserve"'
            className="text-black rounded-sm px-2 py-0.5 w-56 border-2 border-dark-grey"
          />
        </label>
        <Btn onClick={() => setKeywordApplied(keyword.trim())}>Search</Btn>
        {keywordApplied && (
          <Btn
            onClick={() => {
              setKeyword("");
              setKeywordApplied("");
            }}
          >
            Clear
          </Btn>
        )}
        <span className="text-xs text-skin-link">
          server-side keyword search over the window
        </span>
      </div>

      {/* Events. The boundary is keyed by group so a failure in one group shows a
          message instead of crashing the viewer, and switching groups clears it.
          Leland's hard rule: the Logs button must never crash the app. */}
      <div className="flex-1 min-h-0">
        {source ? (
          <LogsErrorBoundary key={source.id} label={source.label}>
            <CwLogsBrowser
              groupId={source.cwGroupId}
              windowId={windowId}
              filterPattern={keywordApplied}
            />
          </LogsErrorBoundary>
        ) : (
          !groupsLoading && (
            <div className="text-skin-link italic p-4">
              Pick a log group to view its events.
            </div>
          )
        )}
      </div>

      {/* Analytics */}
      <LogVolumeChartModal
        isOpen={volumeOpen}
        onClose={() => setVolumeOpen(false)}
        source={source}
        cwWindowId={windowId}
      />
      <LogErrorsChartModal
        isOpen={errorsOpen}
        onClose={() => setErrorsOpen(false)}
        source={source}
        cwWindowId={windowId}
      />
    </div>
  );
};
